import React, { useState } from "react";

import { CATEGORIES_AFFICHAGE, COULEURS_CATEGORIES } from "./categoriesFlux";
import { BulleFlottante, useBulle } from "./BulleFlottante";
import {
  STATUTS_META,
  agregerGroupes,
  jobsDeCategorie,
  statutCategorie,
  statutPrincipal,
  texteDernierBuild,
} from "./filtresMap";

// Nom court d'un traitement pour les bulles (prefixe iCTI_ retire).
const nomCourt = (job) =>
  String(job.filename || job.name || "").replace(/^iCTI_(synchronize_prodsgbd_)?/i, "");

// Colonnes visibles : categories filtrees si un filtre categorie est actif (Kiwee ignore ici).
export function colonnesMatrice(categories) {
  const choisies = (Array.isArray(categories) ? categories : []).map((c) => c.toUpperCase());
  const cols = CATEGORIES_AFFICHAGE.filter((c) => choisies.includes(c));
  return cols.length ? cols : CATEGORIES_AFFICHAGE;
}

function Cellule({ env, categorie, onCellule, montrer, cacher }) {
  const etat = statutCategorie(env, categorie);
  if (!etat) {
    return (
      <div className="map-cell vide" aria-hidden="true">
        –
      </div>
    );
  }
  const couleur = STATUTS_META[etat.statut].couleur;
  const jobs = jobsDeCategorie(env, categorie);
  return (
    <button
      type="button"
      className={`map-cell${etat.running ? " running" : ""}`}
      data-testid={`cellule-${env.key}-${categorie}`}
      style={{
        background: `${couleur}1f`,
        color: etat.statut === "ok" ? "#23825a" : couleur,
        boxShadow: `inset 0 0 0 1px ${couleur}55`,
      }}
      onClick={() => onCellule(env, categorie)}
      onMouseMove={(ev) =>
        montrer(
          ev,
          <div>
            <b>
              {env.group} · {env.env} · {categorie}
            </b>
            {jobs.slice(0, 8).map((j, i) => (
              <div key={i}>
                <span
                  className="map-dot"
                  style={{ background: STATUTS_META[statutPrincipal(j)].couleur }}
                />{" "}
                {nomCourt(j)}
              </div>
            ))}
            {jobs.length > 8 && <div>… {jobs.length - 8} autre(s)</div>}
          </div>
        )
      }
      onMouseLeave={cacher}
    >
      {etat.n}
      {etat.erreurs ? <span className="map-chip-e">{etat.erreurs}</span> : null}
    </button>
  );
}

// Matrice des flux : une ligne par environnement, une colonne par categorie, couleur = pire
// statut ; groupes en anomalie en tete, repliables. onCellule(env, categorie) ; onEnv(env)
// pour un environnement sans traitement.
export default function VueMatrice({ envs, categories, onCellule, onEnv }) {
  const [replies, setReplies] = useState(() => new Set());
  const { bulle, montrer, cacher } = useBulle();
  const groupes = agregerGroupes(envs);
  const cols = colonnesMatrice(categories);
  const basculer = (g) =>
    setReplies((s) => {
      const n = new Set(s);
      if (n.has(g)) n.delete(g);
      else n.add(g);
      return n;
    });
  if (!groupes.length) return <div className="map-vide">Aucun traitement ne correspond aux filtres</div>;
  return (
    <div className="map-scroll" data-testid="vue-matrice">
      <table className="map-mx">
        <thead>
          <tr>
            <th>Groupe / environnement</th>
            <th>Serveur</th>
            {cols.map((c) => (
              <th key={c} className="c">
                <span className="map-dot" style={{ background: COULEURS_CATEGORIES[c] }} /> {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupes.map((g) => {
            const ferme = replies.has(g.group);
            return (
              <React.Fragment key={g.group}>
                <tr className="gr" onClick={() => basculer(g.group)}>
                  <td colSpan={2 + cols.length}>
                    <span style={{ display: "inline-block", width: 14 }}>{ferme ? "▸" : "▾"}</span>
                    <span className="map-dot" style={{ background: STATUTS_META[g.statut].couleur }} />{" "}
                    {g.group}
                    <span className="cnt">
                      {g.envs.length} env. · {g.nb} traitements
                      {g.erreurs ? (
                        <span style={{ color: "#e5484d" }}> · {g.erreurs} en erreur</span>
                      ) : null}
                    </span>
                  </td>
                </tr>
                {!ferme &&
                  g.envs.map((e) => (
                    <tr key={e.key}>
                      <td style={{ paddingLeft: 30 }}>
                        {e.env} <span style={{ color: "#6b7785" }}>{e.vague}</span>
                        {e.nonRemonte && (
                          <button
                            type="button"
                            className="map-badge-nr"
                            onClick={() => onEnv(e)}
                            title={texteDernierBuild(e.dernierBuild)}
                          >
                            Non remonté
                          </button>
                        )}
                      </td>
                      <td style={{ color: "#6b7785" }}>{e.server}</td>
                      {e.sans ? (
                        <td colSpan={cols.length}>
                          <button type="button" className="map-env-sans" onClick={() => onEnv(e)}>
                            Sans traitement
                          </button>
                        </td>
                      ) : (
                        cols.map((c) => (
                          <td key={c}>
                            <Cellule
                              env={e}
                              categorie={c}
                              onCellule={onCellule}
                              montrer={montrer}
                              cacher={cacher}
                            />
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <BulleFlottante bulle={bulle} />
    </div>
  );
}
